import React from 'react';
import ReactEcharts from 'echarts-for-react';
import 'echarts/lib/component/title';
import 'echarts/lib/component/tooltip';
import { useTheme } from '../../../../theme-context';
import NoDataOverlay from '../components/NoDataLayer';
import { useGetCurrentUserQuery } from '../../../../store/user/userApi';

interface MonthlyData {
    month: number;
    label: string;
    reconciled: number;
    potentiallyMatch: number;
    unreconciled: number;
    voided: number;
}

interface IBarChart {
    data: MonthlyData[];
    departments?: Array<string>;
}

const StackedBarChart = ({ data, departments }: IBarChart) => {
    const { theme } = useTheme();
	const {data: user} = useGetCurrentUserQuery();

    const hasData = data?.some((item: any) =>
        ['reconciled', 'potentiallyMatch', 'unreconciled', 'voided'].some((key: string) => item?.[key] > 0)
    );

    const actualData = hasData ? data : [];
    const actualDepartments = hasData ? departments : [];

	const departmentsModified = user?.username === 'westworld' ? actualDepartments?.map(department => (
		department === 'Tempus Bar' ? 'Bar' : 
		department === 'Soul Garden' ? 'Restaurant' : 
		department === 'ERVA' ? 'Cafe' : 
		department === 'BKF 7C' || department === 'Room Service' ? 'Others' : department
	)) : actualDepartments;

    const getOptionBar = () => ({
        grid: {
            left: '3%',
            right: '3%',
            bottom: 100,
            containLabel: true
        },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: {
            data: ['Reconciled', 'Potential Match', 'Unreconciled', 'Voided'],
            textStyle: { color: theme === 'dark' ? '#e4e4e4' : '#252B37' }
        },
        xAxis: {
            type: 'category',
            data: departmentsModified,
            axisLabel: {
                interval: 0,
                rotate: 0,
                margin: 12,
                width: 90,
                overflow: 'break',
                lineHeight: 14,
                formatter: (value: string) => {
                    const style = value.length > 18 ? 'small' : 'normal';
                    return `{${style}|${value}}`;
                },
                rich: {
                    normal: { fontSize: 12 },
                    small: { fontSize: 10 }
                }
            }
        },
        yAxis: { type: 'value' },
        series: [
            {
                name: 'Reconciled',
                type: 'bar',
                stack: 'total',
                data: actualData.map(d => d?.reconciled),
                color: '#10B981',
                itemStyle: {
                    opacity: hasData ? 1 : 0.4,
                    borderColor: '#fff',
                    borderWidth: 1
                }
            },
            {
                name: 'Potential Match',
                type: 'bar',
                stack: 'total',
                data: actualData.map(d => d?.potentiallyMatch),
                color: '#F59E0B',
                itemStyle: {
                    opacity: hasData ? 1 : 0.4,
                    borderColor: '#fff',
                    borderWidth: 1
                }
            },
            {
                name: 'Unreconciled',
                type: 'bar',
                stack: 'total',
                data: actualData.map(d => d?.unreconciled),
                color: '#EF4444',
                itemStyle: {
                    opacity: hasData ? 1 : 0.4,
                    borderColor: '#fff',
                    borderWidth: 1
                }
            },
            {
                name: 'Voided',
                type: 'bar',
                stack: 'total',
                data: actualData.map(d => d?.voided),
                color: '#545863',
                itemStyle: {
                    opacity: hasData ? 1 : 0.4,
                    borderColor: '#fff',
                    borderWidth: 1
                }
            }
        ]
    });

    return (
        <div className="relative h-[400px] w-full">
            <ReactEcharts option={getOptionBar()} style={{ height: 400, width: '100%' }} />
            {!hasData && <NoDataOverlay />}
        </div>
    );
};

export default StackedBarChart;